/**
 * Multi-tile stamps for the Village Maps editor.
 * Patterns use the same outdoor legend chars as the bake pipeline.
 * Space = skip (do not write that cell).
 *
 * House stamps are named by ownership/role (who lives there), not art kit.
 * Kit (gold/moss/slate) stays as secondary art metadata — ASCII chars still
 * encode the kit for the mobile bake pipeline.
 *
 * Shop / school stamps use clear place names with buildingId for export;
 * doorTarget may point at a future interior (authoring-only until wired).
 */

export type VillageHomeId =
  | 'home-pog-yawg'
  | 'home-player'
  | 'home-niam-txiv';

export type VillageHouseOwner = 'pog-yawg' | 'player' | 'niam-txiv';

export type VillageHouseKit = 'gold' | 'moss' | 'slate';

export type VillageShopId = 'shop-clothing';

export type VillageShopOwner = 'clothing-store';

/** Peach plaster + fabric awning storefront kit. */
export type VillageShopKit = 'peach';

export type VillageSchoolId = 'school';

/** Honey thatch + peach plaster schoolhouse kit (bell / sign cue). */
export type VillageSchoolKit = 'school';

export type MapStampHouseMeta = {
  kind: 'house';
  /** Interior / door-target id used by the mobile game. */
  homeId: VillageHomeId;
  /** Same as homeId — canonical building id for export. */
  buildingId: VillageHomeId;
  /** Short owner key (matches VillageHouse.owner). */
  owner: VillageHouseOwner;
  /** Art kit — secondary; ASCII façade chars encode this. */
  kit: VillageHouseKit;
  /** Door legend char written by this stamp. */
  doorChar: 'D' | 'e' | 'o';
  /** Same as homeId — warp/enter target for agents. */
  doorTarget: VillageHomeId;
};

export type MapStampShopMeta = {
  kind: 'shop';
  buildingId: VillageShopId;
  owner: VillageShopOwner;
  kit: VillageShopKit;
  doorChar: 'Z';
  /** Future interior id — scenic solid until a shop interior ships. */
  doorTarget: VillageShopId;
};

export type MapStampSchoolMeta = {
  kind: 'school';
  buildingId: VillageSchoolId;
  /** Public building — no household owner. */
  owner: '';
  kit: VillageSchoolKit;
  doorChar: 'd';
  /** Future interior id — authoring-only façade until a school interior ships. */
  doorTarget: VillageSchoolId;
};

export type MapStampBuildingMeta =
  | MapStampHouseMeta
  | MapStampShopMeta
  | MapStampSchoolMeta;

export type MapStamp = {
  id: string;
  /** Primary UI / export display name (ownership for houses). */
  label: string;
  /** Secondary line (Hmong names, kit, etc.). */
  subtitle?: string;
  /** Rows of equal width; ' ' means leave existing cell unchanged. */
  pattern: string[];
  /** Present on house / shop / school stamps that write buildings[]. */
  building?: MapStampBuildingMeta;
  /**
   * @deprecated Prefer `building` (kind: 'house'). Kept for older call sites.
   */
  house?: MapStampHouseMeta;
};

export const MAP_STAMPS: MapStamp[] = [
  {
    id: 'house-pog-yawg',
    label: "Grandma & Grandpa's house",
    subtitle: 'Pog & Yawg · gold kit',
    // 7×4 façade: unique wall columns per bay (eaves longhouse).
    pattern: ['[l†^‡f]', 'αβЂγЃδε', 'ζηЄQЉθι', 'κλЊDЋμν'],
    building: {
      kind: 'house',
      homeId: 'home-pog-yawg',
      buildingId: 'home-pog-yawg',
      owner: 'pog-yawg',
      kit: 'gold',
      doorChar: 'D',
      doorTarget: 'home-pog-yawg',
    },
  },
  {
    id: 'house-player',
    label: "Player's house",
    subtitle: 'Your farm home · terracotta thatch (moss kit id)',
    pattern: ['{$•~‰%}', 'ξοЌπЍρς', 'τυЎyЏφχ', 'ψωБeГÀÁ'],
    building: {
      kind: 'house',
      homeId: 'home-player',
      buildingId: 'home-player',
      owner: 'player',
      kit: 'moss',
      doorChar: 'e',
      doorTarget: 'home-player',
    },
  },
  {
    id: 'house-niam-txiv',
    label: "Mom & Uncle's house",
    subtitle: 'Niam & Txiv ntxawm · slate-blue thatch',
    pattern: ['(&¤_¥+)', 'ÂÃДÄЖÅÆ', 'ÇÈЗaИÉÊ', 'ËÌЙoКÍÎ'],
    building: {
      kind: 'house',
      homeId: 'home-niam-txiv',
      buildingId: 'home-niam-txiv',
      owner: 'niam-txiv',
      kit: 'slate',
      doorChar: 'o',
      doorTarget: 'home-niam-txiv',
    },
  },
  {
    id: 'shop-clothing',
    label: 'Clothing store',
    subtitle: 'Hmong clothing / tiab · peach kit',
    // 3×4 façade: roof / eaves-upper / door-top / door (‹/› = wallLow4).
    pattern: ['<=>', 'ppp', 'wzw', '‹Z›'],
    building: {
      kind: 'shop',
      buildingId: 'shop-clothing',
      owner: 'clothing-store',
      kit: 'peach',
      doorChar: 'Z',
      doorTarget: 'shop-clothing',
    },
  },
  {
    id: 'school',
    label: 'School',
    subtitle: 'Village schoolhouse · school kit',
    // 3×4 façade: roof / eaves-upper / door-top / door (σ = wallLow5).
    pattern: ['EGI', 'rrr', 'sgs', 'σdσ'],
    building: {
      kind: 'school',
      buildingId: 'school',
      owner: '',
      kit: 'school',
      doorChar: 'd',
      doorTarget: 'school',
    },
  },
  {
    id: 'cave-mouth',
    label: 'Cave mouth',
    pattern: ['jJi', 'nNu'],
  },
  {
    id: 'boat',
    label: 'Boat',
    pattern: ['Kk'],
  },
  {
    id: 'well',
    label: 'Well',
    subtitle: 'Town landmark · draws ~2×',
    // Single walk/collision cell; game + MapGrid scale the sprite ~2×
    // (bottom-anchored, spills into neighbors — same idea as trees).
    pattern: ['L'],
  },
];

// Keep deprecated `house` alias in sync for call sites that still read it.
for (const stamp of MAP_STAMPS) {
  if (stamp.building?.kind === 'house') {
    stamp.house = stamp.building;
  }
}

/** Legacy kit-named ids → current ownership ids. */
const STAMP_ID_ALIASES: Record<string, string> = {
  'house-gold': 'house-pog-yawg',
  'house-moss': 'house-player',
  'house-slate': 'house-niam-txiv',
};

export const MAP_STAMP_BY_ID: Record<string, MapStamp> = Object.fromEntries(
  MAP_STAMPS.map((s) => [s.id, s]),
);

for (const [alias, canonical] of Object.entries(STAMP_ID_ALIASES)) {
  const stamp = MAP_STAMP_BY_ID[canonical];
  if (stamp) MAP_STAMP_BY_ID[alias] = stamp;
}

/** Door legend char → building stamp (houses + shops + school). */
export const BUILDING_STAMP_BY_DOOR_CHAR: Record<string, MapStamp> = {};
for (const stamp of MAP_STAMPS) {
  if (stamp.building) {
    BUILDING_STAMP_BY_DOOR_CHAR[stamp.building.doorChar] = stamp;
  }
}

/** @deprecated Prefer BUILDING_STAMP_BY_DOOR_CHAR (includes shops / school). */
export const HOUSE_STAMP_BY_DOOR_CHAR: Record<string, MapStamp> = {};
for (const stamp of MAP_STAMPS) {
  if (stamp.building?.kind === 'house') {
    HOUSE_STAMP_BY_DOOR_CHAR[stamp.building.doorChar] = stamp;
  }
}

/** Legend chars that are covered by stamps — keep under Advanced. */
export const STAMP_COVERED_CHARS = new Set([
  '[',
  'l',
  '†',
  '^',
  '‡',
  'f',
  ']',
  'U',
  'H',
  'O',
  'Q',
  'D',
  'α',
  'β',
  'Ђ',
  'γ',
  'Ѓ',
  'δ',
  'ε',
  'ζ',
  'η',
  'Є',
  'Љ',
  'θ',
  'ι',
  'κ',
  'λ',
  'Њ',
  'Ћ',
  'μ',
  'ν',
  '{',
  '$',
  '•',
  '~',
  '‰',
  '%',
  '}',
  'V',
  'h',
  '0',
  'y',
  'e',
  'ξ',
  'ο',
  'Ќ',
  'π',
  'Ѝ',
  'ρ',
  'ς',
  'τ',
  'υ',
  'Ў',
  'Џ',
  'φ',
  'χ',
  'ψ',
  'ω',
  'Б',
  'Г',
  'À',
  'Á',
  '(',
  '&',
  '¤',
  '_',
  '¥',
  '+',
  ')',
  'x',
  'q',
  ';',
  'a',
  'o',
  'Â',
  'Ã',
  'Д',
  'Ä',
  'Ж',
  'Å',
  'Æ',
  'Ç',
  'È',
  'З',
  'И',
  'É',
  'Ê',
  'Ë',
  'Ì',
  'Й',
  'К',
  'Í',
  'Î',
  '<',
  '=',
  '>',
  'p',
  'w',
  'z',
  'Z',
  '‹',
  '›',
  'E',
  'G',
  'I',
  'r',
  's',
  'g',
  'd',
  'σ',
  'j',
  'J',
  'i',
  'n',
  'N',
  'u',
  'K',
  'k',
  'L',
]);

export function stampSize(stamp: MapStamp): { w: number; h: number } {
  const h = stamp.pattern.length;
  const w = Math.max(0, ...stamp.pattern.map((row) => row.length));
  return { w, h };
}

export function stampFits(
  stamp: MapStamp,
  originX: number,
  originY: number,
  mapW: number,
  mapH: number,
): boolean {
  const { w, h } = stampSize(stamp);
  return originX >= 0 && originY >= 0 && originX + w <= mapW && originY + h <= mapH;
}

/** Cells the stamp would write (non-space pattern cells). */
export function stampWriteCells(
  stamp: MapStamp,
  originX: number,
  originY: number,
): Array<{ x: number; y: number; ch: string }> {
  const out: Array<{ x: number; y: number; ch: string }> = [];
  for (let dy = 0; dy < stamp.pattern.length; dy += 1) {
    const row = stamp.pattern[dy] ?? '';
    for (let dx = 0; dx < row.length; dx += 1) {
      const ch = row[dx] ?? ' ';
      if (ch === ' ') continue;
      out.push({ x: originX + dx, y: originY + dy, ch });
    }
  }
  return out;
}

/** Door cell offset within a building stamp pattern, or null. */
export function stampDoorOffset(
  stamp: MapStamp,
): { dx: number; dy: number } | null {
  const doorChar = stamp.building?.doorChar ?? stamp.house?.doorChar;
  if (!doorChar) return null;
  for (let dy = 0; dy < stamp.pattern.length; dy += 1) {
    const row = stamp.pattern[dy] ?? '';
    for (let dx = 0; dx < row.length; dx += 1) {
      if (row[dx] === doorChar) return { dx, dy };
    }
  }
  return null;
}

export type PlacedBuilding = {
  stampId: string;
  displayName: string;
  kind: 'house' | 'shop' | 'school';
  buildingId: string;
  /** Houses only — kept for export / seed compatibility. */
  homeId?: VillageHomeId;
  owner: string;
  kit: string;
  doorTarget: string;
  doorChar: string;
  /** Stamp top-left. */
  origin: { x: number; y: number };
  /** Door cell in map coords. */
  door: { x: number; y: number };
};

export function buildingFromStamp(
  stamp: MapStamp,
  originX: number,
  originY: number,
): PlacedBuilding | null {
  const meta = stamp.building ?? stamp.house;
  if (!meta) return null;
  const off = stampDoorOffset(stamp);
  if (!off) return null;
  const base = {
    stampId: stamp.id,
    displayName: stamp.label,
    buildingId: meta.buildingId,
    owner: meta.owner,
    kit: meta.kit,
    doorTarget: meta.doorTarget,
    doorChar: meta.doorChar,
    origin: { x: originX, y: originY },
    door: { x: originX + off.dx, y: originY + off.dy },
  };
  if (meta.kind === 'house') {
    return { ...base, kind: 'house', homeId: meta.homeId };
  }
  return { ...base, kind: meta.kind };
}

/** @deprecated Prefer buildingFromStamp. */
export function buildingFromHouseStamp(
  stamp: MapStamp,
  originX: number,
  originY: number,
): PlacedBuilding | null {
  return buildingFromStamp(stamp, originX, originY);
}

/** Drop buildings whose door cell was overwritten / erased. */
export function pruneBuildingsAfterPaint(
  buildings: PlacedBuilding[] | undefined,
  rows: string[],
  painted: Array<{ x: number; y: number }>,
): PlacedBuilding[] | undefined {
  if (!buildings?.length) return buildings;
  const touched = new Set(painted.map((c) => `${c.x},${c.y}`));
  const next = buildings.filter((b) => {
    const key = `${b.door.x},${b.door.y}`;
    if (!touched.has(key)) return true;
    const ch = rows[b.door.y]?.[b.door.x];
    return ch === b.doorChar;
  });
  return next.length === buildings.length ? buildings : next;
}

/**
 * Upsert a building placement: remove any building whose footprint/door
 * overlaps the new stamp, then append the new entry.
 */
export function upsertBuildingPlacement(
  buildings: PlacedBuilding[] | undefined,
  placed: PlacedBuilding,
  stamp: MapStamp,
): PlacedBuilding[] {
  const { w, h } = stampSize(stamp);
  const ox = placed.origin.x;
  const oy = placed.origin.y;
  const overlaps = (b: PlacedBuilding) => {
    if (b.door.x === placed.door.x && b.door.y === placed.door.y) return true;
    if (b.buildingId && placed.buildingId && b.buildingId === placed.buildingId) {
      return true;
    }
    if (b.homeId && placed.homeId && b.homeId === placed.homeId) return true;
    // Rough footprint overlap with the new stamp rect (legacy → 7×4 houses).
    const prev = MAP_STAMP_BY_ID[b.stampId];
    const prevSize = prev ? stampSize(prev) : { w: 7, h: 4 };
    const bw = prevSize.w;
    const bh = prevSize.h;
    const bx = b.origin?.x ?? b.door.x - 1;
    const by = b.origin?.y ?? b.door.y - (bh - 1);
    return !(bx + bw <= ox || ox + w <= bx || by + bh <= oy || oy + h <= by);
  };
  const kept = (buildings ?? []).filter((b) => !overlaps(b));
  return [...kept, placed];
}
